// Copied from Acend
import { useField } from "formik";

import LabeledMultiSelect, {
  LabeledMultiSelectProps,
} from "./LabeledMultiSelect";
import { Options } from "./Option";

interface MultiSelectFieldProps<T extends Options>
  extends Omit<LabeledMultiSelectProps<T>, "setValue" | "value"> {}

/**
 * Wrapper around the component-library `<LabeledMultiSelectInput>` that injects common Formik field values and methods (`value`, `onBlur`, `onChange`) and error display logic (`errorMessage`, `isValid`) when nested inside a `<Formik>` provider. Any `LabeledMultiSelectProps` provided will either override the injected Formik props or will be passed through.
 *
 * @param props `LabeledMultiSelectProps`
 * @param props.name input name attribute matching Formik field name
 * @param props.options object to create options from. Object keys are used as display text and values as the value
 * @param props.optionsConfig Config object for altering how options are created. Note: `unspecified` defaults to `"REMOVE"` (instead of `"EMPTY_DISPLAY"`)
 * @example <MultiSelectField
              label="Example"
              name="example"
              options={{
                OptionOne: "OPTION_ONE",
                OptionTwo: "OPTION_TWO",
              }}
              optionsConfig={{
                keyFormatting: "CAPITAL_CASE",
                overrides: {
                  OptionTwo: "Option TWO"
                },
                unspecified: "REMOVE"
              }}
              {...passThroughAndOverrideProps}
            />
 */
const MultiSelectField = <T extends Options>({
  name,
  options,
  optionsConfig,
  onChange,
  ...props
}: MultiSelectFieldProps<T>) => {
  const [field, meta, { setValue, setTouched }] = useField(name);

  return (
    <LabeledMultiSelect
      {...field}
      label={props.label || ""}
      errorMessage={meta.error}
      isInvalid={meta.touched && !!meta.error}
      name={name}
      onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
        setTouched(true);
        field.onBlur(e);
      }}
      options={options}
      value={field.value}
      setValue={(value: string[]) => setValue(value)}
      onChange={onChange}
      optionsConfig={optionsConfig}
      // pass through props and allow overrides
      {...props}
    />
  );
};

export default MultiSelectField;
